import type { QueryBuilder } from "@cgarciagarcia/react-query-builder";

import { api } from "~/api";
import {
  adminProvidersResponseSchema,
  convertSlotResponseSchema,
  providerAppointmentsResponseSchema,
  providerScheduleResponseSchema,
  providerSlotsResponseSchema,
} from "./schemas";
import type { ConvertProviderSlotParams, ProviderSlotFilters } from "./types";

export const getAdminProviders = async ({
  queryBuilder,
}: {
  queryBuilder?: QueryBuilder<Record<string, string> | undefined>;
}) => {
  const response = await api.get(
    `/admin/providers${queryBuilder?.build() ?? ""}`,
  );

  return adminProvidersResponseSchema.parse(response.data);
};

export const uploadProviderSchedule = async ({
  providerId,
  file,
  description,
}: {
  providerId: string;
  file: File;
  description?: string;
}) => {
  const formData = new FormData();
  formData.append("file", file);
  if (description) {
    formData.append("description", description);
  }

  const response = await api.post(
    `/admin/providers/${providerId}/schedules`,
    formData,
    { headers: { "Content-Type": file.type } },
  );
  return response.data;
};

export const getProviderScheduleMeta = async (providerId: string) => {
  const response = await api.get(`/admin/providers/${providerId}/schedules`);

  return providerScheduleResponseSchema.parse(response.data);
};

export const exportProviderSchedule = async (providerId: string) => {
  return api.get(`/admin/providers/${providerId}/schedules/file`, {
    responseType: "blob",
  });
};

export const downloadScheduleChangeLog = async (providerId: string) => {
  return api.get(`/admin/providers/${providerId}/schedules/changelog`, {
    responseType: "blob",
  });
};

export const deleteProviderSchedule = async (providerId: string) => {
  return api.delete(`/admin/providers/${providerId}/schedules`);
};

export const getProviderSlots = async ({
  providerId,
  startDate,
  visitTypes = [],
  statuses = [],
}: {
  providerId: string;
  startDate: string;
} & Partial<ProviderSlotFilters>) => {
  const response = await api.get(`/admin/providers/${providerId}/slots`, {
    params: {
      startDate,
      ...(visitTypes.length > 0 && { visitTypes }),
      ...(statuses.length > 0 && { statuses }),
    },
  });

  return providerSlotsResponseSchema.parse(response.data);
};

export const convertProviderSlot = async ({
  providerId,
  slotDate,
  startTime,
  currentAppointmentTypeSlug,
}: ConvertProviderSlotParams) => {
  const response = await api.post(
    `/admin/providers/${providerId}/slots/convert`,
    {
      slot_date: slotDate,
      start_time: startTime,
      current_appointment_type_slug: currentAppointmentTypeSlug,
    },
  );

  return convertSlotResponseSchema.parse(response.data);
};

export const getProviderAppointments = async ({
  providerId,
  orphaned,
}: {
  providerId: string;
  orphaned?: boolean;
}) => {
  const params = new URLSearchParams();

  if (orphaned !== undefined) {
    params.append("orphaned", String(orphaned));
  }

  const queryString = params.toString();
  const url =
    queryString.length > 0
      ? `/admin/providers/${providerId}/appointments?${queryString}`
      : `/admin/providers/${providerId}/appointments`;
  const response = await api.get(url);

  return providerAppointmentsResponseSchema.parse(response.data);
};
